/**
 * Large UPS section descriptor (#997 — Story 4.3).
 *
 * The real, live `SectionDescriptor` for the Large UPS dynamic section. It
 * mirrors the Liquid Cooling descriptor (Story 4.2) but binds the ACTUAL Large
 * UPS per-unit field set, table, mapper, REST contract, and attachment card,
 * so the shared descriptor engine can drive Large UPS at byte-identical parity
 * with the pre-engine bespoke implementation (Story 4.3 golden masters are the
 * oracle).
 *
 * Field source of truth: `FACILITY_POWER_FIELDS` (`facility-power.fields.ts`)
 * plus the editor slice at `LARGE_UPS_EDITOR_SUBSECTION_FIELDS[2..]` — every
 * per-unit field is typed here (text / multilineText / yesNo) to match the
 * real controls in `LargeUpsEditFormContent.tsx`. `name` (user-defined
 * instance name) and `status` (Existing/New) are the engine's identity fields
 * and are handled by the engine list chrome, so they are NOT per-unit
 * descriptor `fields` (they are added back only on the Summary surface via
 * the identity-field label bridge in `largeUpsDescriptorSummary`).
 *
 * `powerAdditionalInformation` is a PER-UNIT "Additional Information" note on
 * `LargeUpsConfiguration` (#997 Bug 2 semantics via migration 109), so it
 * lives on the per-unit fields list and is declared via `additionalInfoField`.
 * Large UPS has NO shared request-level field (no analog to LC's
 * `lcLiquidToChipDesired`), so `sharedFieldKeys` is `[]`.
 *
 * NFR7: the table (`request_large_ups`), mapper
 * (`large-ups-configuration.mapper`), and permission config are read-only;
 * this descriptor only DECLARES the binding.
 */

import { locationTagDescriptorField } from './sectionDescriptor.types';
import {
  LARGE_UPS_TABLE_COLUMN_ALIGN,
  formatLargeUpsTableCell,
  getLargeUpsColumnSubline,
  largeUpsColumnUsesDoubleLine,
  largeUpsTableColumnsForLayout,
  largeUpsTableHeaderLabel,
} from '../../components/largeups/largeUpsListUtils';
import {
  LARGE_UPS_CONFIGURATIONS_LIST_TITLE,
  LARGE_UPS_DELETE_CONFIRM_MESSAGE,
  LARGE_UPS_DELETE_CONFIRM_TITLE,
  LARGE_UPS_DISCARD_UNSAVED_DISCARD_LABEL,
  LARGE_UPS_DISCARD_UNSAVED_KEEP_EDITING_LABEL,
  LARGE_UPS_DISCARD_UNSAVED_MESSAGE,
  LARGE_UPS_DISCARD_UNSAVED_TITLE,
  LARGE_UPS_EDIT_ALL_UNITS_LABEL,
  LARGE_UPS_EDIT_DELETE_LABEL,
  LARGE_UPS_EDIT_HEADER_TITLE_NEW,
  LARGE_UPS_EDIT_SAVE_DRAFT_LABEL,
  LARGE_UPS_EDIT_SAVE_LABEL,
  LARGE_UPS_EMPTY_STATE_TITLE,
} from '../../constants/largeUpsConfigurationsSubsectionCopy';
import { SECTION_IDS } from '../../constants/sectionIds';
import { LARGE_UPS_EDITOR_SUBSECTION_FIELDS } from '../fields/requestFieldRegistry';
import { FACILITY_POWER_FIELDS } from '../fields/steps/facility-power.fields';

import type {
  SectionDescriptor,
  SectionDescriptorField,
  SectionDescriptorFieldType,
  SectionDescriptorFieldValidation,
  SectionListColumnsProjection,
} from './sectionDescriptor.types';
import type { LargeUpsTableColumnKey } from '../../components/largeups/largeUpsListUtils';
import type { LargeUpsConfiguration } from '../../types/largeUps';

/**
 * Per-field render type + validation + dropdown options, keyed by dataField.
 * Every non-identity per-unit key covered by the descriptor has an entry here;
 * anything missing falls back to `text`. Types match the real Large UPS
 * controls (see `LargeUpsEditFormContent`).
 */
interface LargeUpsFieldTypeSpec {
  type: SectionDescriptorFieldType;
  options?: readonly string[];
  validation?: SectionDescriptorFieldValidation;
}

const LARGE_UPS_FIELD_TYPES: Readonly<Record<string, LargeUpsFieldTypeSpec>> = {
  // Per-unit Large UPS details (order preserved by LARGE_UPS_EDITOR_SUBSECTION_FIELDS[2..])
  // Types match the live editor controls in LargeUpsEditFormContent.tsx:
  //   - `powerExistingSiteProtectedByLargeKwUps` renders as FormYesNoChoice but is
  //     a 'Yes'/'No' STRING on `LargeUpsConfiguration` (NOT a BIT/boolean like
  //     Small UPS's `smallUpsBackupGenerator`) — the mapper + DB column store the
  //     literal string and the live editor gates `powerModelOfExistingUps` on
  //     `!== 'Yes'`. So it is typed `text` (NOT `yesNo`): #997 Story 6.5's live
  //     swap runs the value through `descriptorFieldMapToPatch`, whose `yesNo`
  //     branch would coerce 'Yes' → boolean `true` and break both the selector
  //     render and the model-field gate. `text` passes the string through verbatim.
  //   - `powerLargeUpsDescription` renders as a multiline FormInput (multilineText)
  //   - `powerModelOfExistingUps` / `powerLevelOfRedundancy` /
  //     `powerRequiredRedundancyForUps` / `powerBatteryChemistry` /
  //     `powerBatteryRuntimeRequiredMin` all render as bare FormInput (text);
  //     LU has no per-instance dropdown option lists (`LARGE_UPS_PLACEHOLDERS`
  //     is placeholder-only) and `powerBatteryRuntimeRequiredMin` is a `string`
  //     on `LargeUpsConfiguration`, not a number.
  //   - `powerAdditionalInformation` renders through RepeatableInstanceAdditionalInfo
  //     which is multilineText.
  powerExistingSiteProtectedByLargeKwUps: { type: 'text' },
  powerLargeUpsDescription: { type: 'multilineText' },
  powerModelOfExistingUps: { type: 'text' },
  powerLevelOfRedundancy: { type: 'text' },
  powerRequiredRedundancyForUps: { type: 'text' },
  powerBatteryChemistry: { type: 'text' },
  powerBatteryRuntimeRequiredMin: { type: 'text' },
  // Per-unit site-power fields (#997 QA-A2, migration 111). Types mirror the live
  // editor controls in LargeUpsEditFormContent.tsx: powerSourceLocation renders as a
  // single-line FormInput (NVARCHAR 255 → text); powerFireSuppressionNeeds renders as
  // a multiline FormInput (NVARCHAR MAX → multilineText).
  powerSourceLocation: { type: 'text' },
  powerFireSuppressionNeeds: { type: 'multilineText' },
  // Per-unit "Additional Information" note (#997 Bug 2, migration 109)
  powerAdditionalInformation: { type: 'multilineText' },
};

/** Registry lookup: dataField → RequestFieldDefinition (for label / subsection). */
const LARGE_UPS_FIELDS_BY_DATA_FIELD = new Map(
  FACILITY_POWER_FIELDS.map(field => [field.dataField, field]),
);

/** Identity fields the engine list chrome owns; NOT in per-unit `fields`. */
const LARGE_UPS_IDENTITY_KEYS = new Set(['name', 'status']);

/**
 * Per-unit descriptor field dataField keys, in editor order. Sourced from
 * `LARGE_UPS_EDITOR_SUBSECTION_FIELDS` minus identity fields (`name`, `status`),
 * plus the note field (`powerAdditionalInformation`) appended.
 */
const PER_UNIT_DATA_FIELD_KEYS: readonly string[] = [
  ...LARGE_UPS_EDITOR_SUBSECTION_FIELDS.filter(
    field => !LARGE_UPS_IDENTITY_KEYS.has(field.key),
  ).map(field => field.key),
  'powerAdditionalInformation',
];

function buildDescriptorField(dataField: string): SectionDescriptorField {
  if (dataField === 'locationTagId') {
    return locationTagDescriptorField('large-ups', 'largeUps');
  }
  const registryField = LARGE_UPS_FIELDS_BY_DATA_FIELD.get(dataField);
  if (!registryField) {
    throw new Error(
      `LARGE_UPS_DESCRIPTOR: no FACILITY_POWER_FIELDS entry for ${dataField}`,
    );
  }
  const spec = LARGE_UPS_FIELD_TYPES[dataField] ?? { type: 'text' };
  return {
    ...registryField,
    type: spec.type,
    options: spec.options,
    validation: spec.validation,
  };
}

/** Typed per-unit descriptor fields (10: 9 per-unit fields + 1 note). */
const LARGE_UPS_DESCRIPTOR_FIELDS: readonly SectionDescriptorField[] =
  PER_UNIT_DATA_FIELD_KEYS.map(buildDescriptorField);

/**
 * Snake_case columns the Large UPS section persists — the exact key set
 * emitted by `large-ups-configuration.mapper.toEntity` and (excluding
 * identity/audit columns) the `request_large_ups` migration 100 + 109 + 111
 * column union. The `descriptorDbBinding` guardrail joins this list to both.
 */
export const LARGE_UPS_DESCRIPTOR_DB_COLUMNS: readonly string[] = [
  'request_id',
  'status',
  'name',
  'location_tag_id',
  'power_existing_site_protected_by_large_kw_ups',
  'power_large_ups_description',
  'power_model_of_existing_ups',
  'power_level_of_redundancy',
  'power_required_redundancy_for_ups',
  'power_battery_chemistry',
  'power_battery_runtime_required_min',
  'power_source_location',
  'power_fire_suppression_needs',
  'power_additional_information',
  'display_order',
];

/**
 * Large UPS list projection (#997 Story 6.5 — parity by reuse). Delegates to the
 * product-approved bespoke table (`largeUpsListUtils`: NAME | MODEL | RUNTIME
 * | REDUNDANCY | EXISTING + compact set, sublines, double-line cells). Engine
 * rows carry identity as `instanceName`/`instanceStatus` (stripped from the Large
 * UPS shape by `largeUpsToDescriptorInstance`), so adapt the row back before the
 * Large-UPS-shaped formatters run — the exact `smallUpsRowForList` pattern.
 */
const largeUpsRowForList = (
  row: Record<string, unknown>,
): LargeUpsConfiguration => {
  const unit = row as unknown as LargeUpsConfiguration;
  return {
    ...unit,
    name: unit.name ?? (row.instanceName as LargeUpsConfiguration['name']),
    status:
      unit.status ?? (row.instanceStatus as LargeUpsConfiguration['status']),
  };
};

const LARGE_UPS_LIST_COLUMNS: SectionListColumnsProjection = {
  locationTagCellColumn: 'ROOM',
  columnsForLayout: largeUpsTableColumnsForLayout,
  headerLabel: (column, layout) =>
    largeUpsTableHeaderLabel(column as LargeUpsTableColumnKey, layout),
  formatCell: (row, column) =>
    formatLargeUpsTableCell(
      largeUpsRowForList(row),
      column as LargeUpsTableColumnKey,
    ),
  columnAlign: LARGE_UPS_TABLE_COLUMN_ALIGN,
  getColumnSubline: (row, column, layout) =>
    getLargeUpsColumnSubline(
      column as LargeUpsTableColumnKey,
      largeUpsRowForList(row),
      layout,
    ),
  columnUsesDoubleLine: (column, layout) =>
    largeUpsColumnUsesDoubleLine(column as LargeUpsTableColumnKey, layout),
};

/** The canonical Large UPS descriptor. */
export const LARGE_UPS_DESCRIPTOR: SectionDescriptor = {
  id: 'large-ups',
  stepId: 'large-ups',
  labels: {
    entitySingular: 'Large UPS',
    entityPlural: 'Large UPS',
    addButtonLabel: 'Add Large UPS',
    emptyStateTitle: LARGE_UPS_EMPTY_STATE_TITLE,
    editFormTitle: LARGE_UPS_EDIT_HEADER_TITLE_NEW,
    editFormSubtitle: LARGE_UPS_CONFIGURATIONS_LIST_TITLE,
    // Chrome copy parity (#997 Story 6.5, mirror Small UPS 6.4 / Racks 6.3): bind
    // the bespoke hand-authored copy so the engine chrome is byte-identical to
    // the pre-swap subsection, not template-coincident.
    editSaveLabel: LARGE_UPS_EDIT_SAVE_LABEL,
    editSaveDraftLabel: LARGE_UPS_EDIT_SAVE_DRAFT_LABEL,
    editDeleteLabel: LARGE_UPS_EDIT_DELETE_LABEL,
    deleteConfirmTitle: LARGE_UPS_DELETE_CONFIRM_TITLE,
    deleteConfirmMessage: LARGE_UPS_DELETE_CONFIRM_MESSAGE,
    editAllListLabel: LARGE_UPS_EDIT_ALL_UNITS_LABEL,
    editDiscardTitle: LARGE_UPS_DISCARD_UNSAVED_TITLE,
    editDiscardMessage: LARGE_UPS_DISCARD_UNSAVED_MESSAGE,
    editDiscardConfirmLabel: LARGE_UPS_DISCARD_UNSAVED_DISCARD_LABEL,
    editDiscardKeepEditingLabel: LARGE_UPS_DISCARD_UNSAVED_KEEP_EDITING_LABEL,
    newInstanceChromeTitle: LARGE_UPS_EDIT_HEADER_TITLE_NEW,
  },
  cardinality: 'many',
  fields: LARGE_UPS_DESCRIPTOR_FIELDS,
  permissionRef: {
    rbacAnchorDataField: 'powerExistingSiteProtectedByLargeKwUps',
  },
  summaryOrder: LARGE_UPS_DESCRIPTOR_FIELDS.map(field => field.dataField),
  pdfLayout: {
    stepId: 'large-ups',
    anchor: 'Large UPS Information',
    perUnitFieldGroupKeys: ['largeUpsInfoFields'],
  },
  // Large UPS has NO request-level shared fields — no analog to LC's
  // `lcLiquidToChipDesired`.
  sharedFieldKeys: [],
  // The live LU editor renders TWO per-unit attachment carousels: the details
  // card at `SECTION_IDS.FACILITY_POWER_LARGE_UPS` and the per-unit Additional
  // Information card at `SECTION_IDS.FACILITY_POWER_LARGE_UPS_ADDITIONAL`, both
  // scoped through `resolveRepeatableInstanceAttachmentSectionId(base, cfgId)`
  // (`LargeUpsEditFormContent.tsx`). Bug 1 / Obs-7 (#997) fixed the additional
  // card, which previously used a flat unit-agnostic id shared across units.
  // Ordering matters: `DescriptorInstanceEditFormBody` derives the per-unit
  // Additional Info section id from `attachmentBaseSectionIds[length - 1]`, so
  // `_ADDITIONAL` MUST be last (mirrors PD / Cooling).
  attachmentsEnabled: true,
  attachmentBaseSectionIds: [
    SECTION_IDS.FACILITY_POWER_LARGE_UPS,
    SECTION_IDS.FACILITY_POWER_LARGE_UPS_ADDITIONAL,
  ],
  listColumns: LARGE_UPS_LIST_COLUMNS,
  additionalInfoField: 'powerAdditionalInformation',
  offlineBinding: {
    restResource: 'large-ups',
    // Large UPS is the THIRD section wired onto the engine save/flush path
    // (#997 Story 6.5). It opts in explicitly via the delegating flush shim
    // `flushLargeUpsDescriptorEditSession` (see `resolveDelegatedFlushBinding`).
    delegatedFlush: true,
    reorderIdsBodyKey: 'largeUpsIds',
    persistedInstancesKey: 'largeUpsConfigurations',
    instanceIdOpField: 'largeUpsId',
    reorderIdsOpField: 'largeUpsIds',
  },
  dbBinding: {
    table: 'request_large_ups',
    columns: LARGE_UPS_DESCRIPTOR_DB_COLUMNS,
  },
};
